import { useEffect, useState, useMemo } from "react";
import { itemsService } from "../../services/items.service";
import { Accordion, AccordionSummary, AccordionDetails, Typography, CircularProgress } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { formatCustomNumber } from "../../helpers/formatCustomNumber";
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Dot 
} from "recharts";

function ItemSalesSummary({ Item_Id }) {
  const [summaryList, setSummaryList] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lastLoadedId, setLastLoadedId] = useState(null);

  useEffect(() => {
    // Logic Check:
    // 1. Must be expanded.
    // 2. Must have an Item_Id.
    // 3. ONLY fetch if Item_Id is different from what we last loaded.
    if (expanded && Item_Id && Item_Id !== lastLoadedId) {
      fetchSummary();
    }
  }, [expanded, Item_Id, lastLoadedId]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await itemsService.getItemSalesOrderSummary(Item_Id);
      if (res?.data) {
        const sortedData = [...res.data].sort((a, b) => a.month_id - b.month_id);
        setSummaryList(sortedData);
        // Record that we successfully loaded this specific ID
        setLastLoadedId(Item_Id);
      }
    } catch (error) {
      console.error("Error fetching summary:", error);
    } finally {
      setLoading(false);
    }
  };
  // Calculate total for the "Direct Sales" box in your screenshot
  const totalSales = useMemo(() => {
    return summaryList.reduce((sum, item) => sum + (item.amount || 0), 0);
  }, [summaryList]);

  return (
    <div className="w-full shadow-sm rounded-lg overflow-hidden border border-gray-200 bg-white mt-4">
      <Accordion elevation={0} expanded={expanded} onChange={(e, val) => setExpanded(val)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} className="bg-gray-50">
          <div className="flex items-center gap-3">
            <Typography className="font-bold text-gray-700">Sales Order Summary</Typography>
            {loading && <CircularProgress size={20} />}
          </div>
        </AccordionSummary>
        
        <AccordionDetails>
          <div className="p-2">
            {/* Header Stat Box (Matching your screenshot) */}
            <div className="flex justify-end mb-4">
              <div className="bg-gray-50 border border-gray-100 p-4 rounded-xl text-right min-w-[200px]">
                <span className="text-gray-500 text-sm font-medium">Total Sales</span>
                <h2 className="text-2xl font-bold text-gray-800">
                  ₦{formatCustomNumber(totalSales)}
                </h2>
              </div>
            </div>

            {/* CHART AREA */}
            <div className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={summaryList} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="month_name_short" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#9ca3af', fontSize: 12 }} 
                    dy={10}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                    tickFormatter={(value) => value >= 1000 ? `${value/1000}K` : value}
                  />
                  <Tooltip 
                    formatter={(value) => [`₦${formatCustomNumber(value)}`, "Amount"]}
                    contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="amount" 
                    stroke="#4f46e5" 
                    strokeWidth={3} 
                    dot={{ r: 6, fill: "#4f46e5", strokeWidth: 2, stroke: "#fff" }}
                    activeDot={{ r: 8, strokeWidth: 0 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
}

export default ItemSalesSummary;